import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import useFetchHandler from "../../../hooks/use-fetch-handler"
import { getFileFromBase64 } from '../utils'

export default function useCapturedImage() {
  const { id: patientId } = useParams()

  const [globalImages, setGlobalImages] = useState({
    left: null,
    right: null,
    crown: null
  })

  // image upload state
  const [analysisId, setAnalysisId] = useState(null)
  const [validationError, setValidationError] = useState(null)

  const {
    error: globalCrownUploadError,
    loading: globalCrownUploading,
    responseSuccess: globalCrownUploadSuccess,
    handleFetch: globalCrownFetchHandler,
    resetRequestState: resetGlobalCrownUploadState,
  } = useFetchHandler()
  const {
    error: globalLeftUploadError,
    loading: globalLeftUploading,
    responseSuccess: globalLeftUploadSuccess,
    handleFetch: globalLeftFetchHandler,
    resetRequestState: resetGlobalLeftUploadState,
  } = useFetchHandler()
  const {
    error: globalRightUploadError,
    loading: globalRightUploading,
    responseSuccess: globalRightUploadSuccess,
    handleFetch: globalRightFetchHandler,
    resetRequestState: resetGlobalRightUploadState,
  } = useFetchHandler()

  const {
    error: analysisCreationError,
    loading: analysisCreationLoading,
    responseSuccess: analysisCreationSuccess,
    handleFetch: analysisCreateFetchHandler,
    resetRequestState: resetAnalysisCreationState,
  } = useFetchHandler()

  const [isImageStreamLoaded, setIsImageStreamLoaded] = useState(false)

  const areImagesBeingUploaded = Boolean(globalCrownUploading || globalLeftUploading || globalRightUploading || analysisCreationLoading);
  const isImageUploadingSuccessful = Boolean(globalCrownUploadSuccess && globalLeftUploadSuccess && globalRightUploadSuccess && analysisCreationSuccess);

  const resetStateToInitials = useCallback(() => {
    resetAnalysisCreationState()
    resetGlobalCrownUploadState()
    resetGlobalLeftUploadState()
    resetGlobalRightUploadState()
    setGlobalImages({ left: null, right: null, crown: null })
    setAnalysisId(null)
    setValidationError(null)
  }, [resetAnalysisCreationState, resetGlobalCrownUploadState, resetGlobalLeftUploadState, resetGlobalRightUploadState])

  const uploadGlobalImages = useCallback(async (analysisId, subType) => {
    const globalImageOfType = globalImages[subType];

    if(!globalImageOfType) {
      throw new Error(`Global ${subType} image is missing`)
    }

    const globalImageFile = getFileFromBase64(globalImageOfType)

    const formData = new FormData();
    formData.append('mainType', "portrait")
    formData.append('subType', subType)
    formData.append('images[0]', globalImageFile)

    switch(subType) {
      case "crown":
        await globalCrownFetchHandler(
          "POST",
          `hair_analysis/${analysisId}/images/upload`,
          formData,
          true,
          (data) => { },
          true
        )
        break;
      case "left":
        await globalLeftFetchHandler(
          "POST",
          `hair_analysis/${analysisId}/images/upload`,
          formData,
          true,
          (data) => { },
          true
        )
        break;
      case "right":
        await globalRightFetchHandler(
          "POST",
          `hair_analysis/${analysisId}/images/upload`,
          formData,
          true,
          (data) => { },
          true
        )
        break;
      default:
        throw new Error(`Global ${subType} image is missing`)
    }
  }, [globalImages, globalCrownFetchHandler, globalLeftFetchHandler, globalRightFetchHandler]);

  const createAnalysisAndUploadImages = useCallback(async () => {
    try {
      if (!globalImages.crown || !globalImages.left || !globalImages.right) {
        throw new Error("Global crown, left or right is missing")
      }

      // if the analysis has already been created, simply upload the images (if not done already)
      if (analysisId) {
        const requests = [];
        if (!globalCrownUploadSuccess) requests.push(
          uploadGlobalImages(analysisId, "crown")
        );
        if (!globalLeftUploadSuccess) requests.push(
          uploadGlobalImages(analysisId, "left")
        );
        if (!globalRightUploadSuccess) requests.push(
          uploadGlobalImages(analysisId, "right")
        );
        await Promise.all(requests)
        return;
      }

      await analysisCreateFetchHandler(
        'POST',
        "hair_analysis/create",
        JSON.stringify({
          "patient_id": Number(patientId),
        }),
        true,
        async (data) => {
          setAnalysisId(data?.id);
          await Promise.all([
            uploadGlobalImages(data?.id, "crown"),
            uploadGlobalImages(data?.id, "left"),
            uploadGlobalImages(data?.id, "right"),
          ])
        },
        false
      )
    } catch (error) {
      setValidationError(error?.message)
    }
  }, [
    analysisCreateFetchHandler, 
    analysisId, 
    globalCrownUploadSuccess, 
    globalImages.crown, 
    globalImages.left, 
    globalImages.right, 
    globalLeftUploadSuccess, 
    globalRightUploadSuccess, 
    patientId, 
    uploadGlobalImages
  ]);

  return {
    globalCrownImage: globalImages.crown,
    globalLeftImage: globalImages.left,
    globalRightImage: globalImages.right,
    setGlobalCrownImage: (image) => setGlobalImages(prev => ({
      ...prev,
      crown: image
    })),
    setGlobalLeftImage: (image) => setGlobalImages(prev => ({
      ...prev,
      left: image
    })),
    setGlobalRightImage: (image) => setGlobalImages(prev => ({
      ...prev,
      right: image
    })),
    uploadImages: createAnalysisAndUploadImages,
    uploadingImages: areImagesBeingUploaded,
    isAnalysisCreated: Boolean(analysisId),
    isImageUploadSuccessful: isImageUploadingSuccessful,
    uploadErrors: {
      global_crown: globalCrownUploadError,
      global_left: globalLeftUploadError,
      global_right: globalRightUploadError,
      analysis_creation: analysisCreationError,
      validation: validationError,
    },
    resetStateToInitials,
    isImageStreamLoaded,
    setIsImageStreamLoaded
  }
}
